import { createFileRoute } from "@tanstack/react-router";
import { UserPlus, Mail, Wallet, Lock, QrCode, PenLine } from "lucide-react";
import { PlatformHub } from "@/components/marketing/PlatformHub";
import heroImg from "@/assets/cards-sharing.webp";

export const Route = createFileRoute("/platform/networking/")({
  head: () => ({
    meta: [
      { title: "Sharing & Networking Tools — banana.cards" },
      { name: "description", content: "Every way to share your card and capture a lead: QR codes, Apple & Google Wallet, email signatures, lead-exchange forms, welcome emails and PIN-protected cards." },
      { property: "og:title", content: "Sharing & Networking Tools — banana.cards" },
      { property: "og:description", content: "QR, Wallet, lead exchange, welcome emails, PIN protection and email signatures — every networking tool in one place." },
      { property: "og:image", content: heroImg },
      { property: "og:url", content: "https://banana.cards/platform/networking" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/platform/networking" }],
  }),
  component: Page,
});

function Page() {
  return (
    <PlatformHub
      eyebrow="Sharing & Networking"
      title="Every way to share,"
      italic="every way to capture."
      description="Six networking tools that live on top of your Banana card — so a tap, a scan or a click always turns into a saved contact, a follow-up, and a relationship."
      heroImage={heroImg}
      heroImageAlt="Overview of Banana sharing and networking tools: NFC cards, QR codes, Wallet passes, email signatures and welcome emails"
      features={[
        {
          title: "Lead Exchange",
          desc: "A two-way form on every card — they get your contact, you get theirs, straight into the CRM.",
          href: "/platform/networking/lead-exchange",
          icon: UserPlus,
          bullets: ["Custom fields", "Auto-sync to CRM", "GDPR consent built-in"],
        },
        {
          title: "Welcome Email",
          desc: "Send a personalized welcome the moment someone scans your card — automatically.",
          href: "/platform/networking/welcome-email",
          icon: Mail,
          bullets: ["Trigger on first tap", "Personalized variables", "Track opens & clicks"],
        },
        {
          title: "Apple & Google Wallet",
          desc: "Drop your card straight into the recipient's Wallet — always at hand, always up to date.",
          href: "/platform/networking/wallet",
          icon: Wallet,
          bullets: ["iOS & Android pass", "Live updates", "No app install"],
        },
        {
          title: "PIN Protection",
          desc: "Lock sensitive cards behind a PIN — perfect for executive, legal or medical profiles.",
          href: "/platform/networking/pin-protection",
          icon: Lock,
          bullets: ["Per-card PIN", "Rotate anytime", "Audit log"],
        },
        {
          title: "QR Code",
          desc: "A high-contrast, branded QR for every card — print it, project it, stick it anywhere.",
          href: "/platform/networking/qr-code",
          icon: QrCode,
          bullets: ["Branded colors & logo", "PNG / SVG / PDF export", "Tracked scans"],
        },
        {
          title: "Email Signature",
          desc: "A polished HTML signature that mirrors your card — for Gmail, Outlook and Apple Mail.",
          href: "/platform/networking/email-signature",
          icon: PenLine,
          bullets: ["One-click install", "Always in sync", "Team templates"],
        },
      ]}
      outro={{ title: "Turn every tap", italic: "into a conversation.", description: "Six tools, one card, zero friction — built so nothing slips through the cracks." }}
    />
  );
}